import {
  DEFAULT_LEARNING_LANGUAGE_CODE,
  LEARNING_LANGUAGE_BY_CODE,
  normalizeLearningLanguageCode,
} from '../constants/learningLanguages';

const EXTERNAL_PROTOCOL_PATTERN = /^[a-z][a-z\d+.-]*:/i;

// Public pages whose canonical English URL has no /en prefix (see routeSeo.js).
// Links to them in English point at the unprefixed URL, never the /en duplicate.
const UNPREFIXED_ENGLISH_PUBLIC_ROOTS = new Set([
  'blog',
  'coach',
  'contact',
  'cookies',
  'faq',
  'learn-english',
  'learning-guide',
  'learning-paths',
  'learning-tips',
  'library',
  'listening-practice',
  'meet-creator',
  'methodology',
  'podcast-lessons',
  'privacy',
  'store',
  'terms',
]);

const PAGE_LANGUAGE_PREFIXES = ['en', 'es', 'no'];

// The language a page's URL declares: its /es or /no prefix, otherwise English.
export function getPageLanguageFromPath(pathname = '/') {
  const firstSegment = String(pathname || '/').split('/')[1]?.toLowerCase() || '';
  return PAGE_LANGUAGE_PREFIXES.includes(firstSegment) ? firstSegment : 'en';
}

function stripPageLanguagePrefix(path) {
  const segments = path.split('/');
  if (PAGE_LANGUAGE_PREFIXES.includes(String(segments[1] || '').toLowerCase())) {
    const stripped = `/${segments.slice(2).join('/')}`;
    return stripped === '/' ? '/' : stripped.replace(/\/{2,}/g, '/');
  }
  return path;
}

function isExternalOrAnchor(path) {
  return path.startsWith('#') || path.startsWith('//') || EXTERNAL_PROTOCOL_PATTERN.test(path);
}

/**
 * Link to a public page in a language, using its canonical URL: English pages
 * listed above are unprefixed (/faq, /podcast-lessons, /), everything else keeps
 * the language prefix (/es/faq, /en/live-classes).
 */
export function buildPublicPath(path, languageCode) {
  if (typeof path !== 'string' || path.length === 0 || isExternalOrAnchor(path)) {
    return path;
  }

  const language = PAGE_LANGUAGE_PREFIXES.includes(languageCode) ? languageCode : 'en';
  const pathWithoutLanguage = stripPageLanguagePrefix(path.startsWith('/') ? path : `/${path}`);
  const root = pathWithoutLanguage.split('/')[1] || '';

  if (pathWithoutLanguage === '/') {
    return language === 'en' ? '/' : `/${language}`;
  }
  if (language === 'en' && UNPREFIXED_ENGLISH_PUBLIC_ROOTS.has(root)) {
    return pathWithoutLanguage;
  }
  return `/${language}${pathWithoutLanguage}`;
}

// Learning-language link: canonical unprefixed URL for English public pages,
// language-prefixed for everything else (dashboards, live classes, lessons).
export function buildPublicLearningPath(path, languageCode) {
  const root = stripPageLanguagePrefix(String(path || '/')).split('/')[1] || '';
  return normalizeLearningLanguageCode(languageCode, '') === 'en' && UNPREFIXED_ENGLISH_PUBLIC_ROOTS.has(root)
    ? buildPublicPath(path, 'en')
    : buildLearningPath(path, languageCode);
}

// Which learning language nav links use: the visitor's chosen learning language,
// or, without a preference, the language of the page they are on. Never the
// route-driven active language.
export function getLinkLearningLanguage(preferredLanguage, pathname) {
  return preferredLanguage || getPageLanguageFromPath(pathname);
}

function hasLearningLanguagePrefix(segment) {
  const normalized = normalizeLearningLanguageCode(segment, '');
  return Boolean(normalized && LEARNING_LANGUAGE_BY_CODE[normalized]);
}

export function stripLearningRoutePrefix(pathname = '/') {
  const normalizedPath = String(pathname || '/').startsWith('/')
    ? String(pathname || '/')
    : `/${pathname || ''}`;
  const segments = normalizedPath.split('/');
  const firstSegment = String(segments[1] || '');

  if (!hasLearningLanguagePrefix(firstSegment)) {
    return normalizedPath;
  }

  const stripped = `/${segments.slice(2).join('/')}`;
  return stripped === '/' ? '/' : stripped.replace(/\/{2,}/g, '/');
}

export function buildLearningPath(
  path,
  languageCode,
  fallbackLanguage = DEFAULT_LEARNING_LANGUAGE_CODE
) {
  if (typeof path !== 'string' || path.length === 0) {
    return path;
  }

  if (
    path.startsWith('#')
    || path.startsWith('//')
    || EXTERNAL_PROTOCOL_PATTERN.test(path)
  ) {
    return path;
  }

  const normalizedLanguage = normalizeLearningLanguageCode(languageCode, fallbackLanguage);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const pathWithoutLanguage = stripLearningRoutePrefix(normalizedPath);

  return pathWithoutLanguage === '/'
    ? `/${normalizedLanguage}`
    : `/${normalizedLanguage}${pathWithoutLanguage}`;
}

export function ensureLearningPath(
  path,
  languageCode,
  fallbackPath = '/podcast-lessons',
  fallbackLanguage = DEFAULT_LEARNING_LANGUAGE_CODE
) {
  const raw = String(path || '').trim();
  const safePath = raw.startsWith('/') ? raw : fallbackPath;
  return buildLearningPath(safePath, languageCode, fallbackLanguage);
}

export function buildLearningUrl(
  origin,
  path,
  languageCode,
  fallbackLanguage = DEFAULT_LEARNING_LANGUAGE_CODE
) {
  return new URL(
    buildLearningPath(path, languageCode, fallbackLanguage),
    origin
  ).toString();
}
